import { useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './elements/Header';
import Footer from './elements/Footer';
import RouteAnalytics from './elements/RouteAnalytics';
import RouteMetadata from './tools/RouteMetadata';
import CookieConsent from './elements/CookieConsent';
import ContactPage from './elements/ContactPage';
import { contactForms } from './data/contactForms';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import Products from './pages/Products';
import Shop from './pages/Shop';
import BalerSelectionGuide from './pages/BalerSelectionGuide';
import ContactUs from './pages/ContactUs';
import RequestQuote from './pages/RequestQuote';
import Questionnaire from './pages/Questionnaire';
import Search from './pages/Search';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ClosedEndManualTieBalers from './pages/Products/ClosedEndManualTieBalers';
import OpenEndAutomaticTieBalers from './pages/Products/OpenEndAutomaticTieBalers';
import WideMouthOpenEndAutomaticTieBalers from './pages/Products/WideMouthOpenEndAutomaticTieBalers';
import MultiMaterialsFullEjectClosedEndBalers from './pages/Products/MultiMaterialsFullEjectClosedEndBalers';
import OpenEndManualTieBalers from './pages/Products/OpenEndManualTieBalers';
import CartLiftersDumpersDumpCarts from './pages/Products/CartLiftersDumpersDumpCarts';
import ConveyorsBaleConveyingBaggingSystems from './pages/Products/ConveyorsBaleConveyingBaggingSystems';
import UsedClothingBalers from './pages/Products/UsedClothingBalers';
import CottonGinTextileBalingPresses from './pages/Products/CottonGinTextileBalingPresses';
import BiomassCottonGinTrashBalingPress from './pages/Products/BiomassCottonGinTrashBalingPress';
import VerticalDownPackingBalingPressesBalers from './pages/Products/VerticalDownPackingBalingPressesBalers';
import PreCrushCompactorsWasteCompactors from './pages/Products/PreCrushCompactorsWasteCompactors';
import CustomDesignedSpecialtyBalers from './pages/Products/CustomDesignedSpecialtyBalers';
import HorizontalManualTieBalers from './pages/SelectionGuide/HorizontalManualTieBalers';
import HorizontalAutomaticTieBalers from './pages/SelectionGuide/HorizontalAutomaticTieBalers';
import MultiMaterialFullEjectBalers from './pages/SelectionGuide/MultiMaterialFullEjectBalers';
import VerticalDownPackingBalers from './pages/SelectionGuide/VerticalDownPackingBalers';
import ServiceDepartment from './pages/Service/ServiceDepartment';
import BalerRefurbishingServices from './pages/Service/BalerRefurbishingServices';
import BalerReplacementParts from './pages/Service/BalerReplacementParts';
import BalingWire from './pages/Service/BalingWire';
import BalingWireQuotation from './pages/Service/BalingWireQuotation';
import AboutUs from './pages/Company/AboutUs';
import DealersAndReps from './pages/Company/DealersAndReps';
import Sustainability from './pages/Company/Sustainability';
import Refurbished from './pages/Company/Refurbished';
import PaperMills from './pages/Industries/PaperMills';
import CorrugatedContainerManufacturers from './pages/Industries/CorrugatedContainerManufacturers';
import NonwovenMills from './pages/Industries/NonwovenMills';
import TextileMillsCottonGins from './pages/Industries/TextileMillsCottonGins';
import DistributionCenters from './pages/Industries/DistributionCenters';
import ManufacturersIndustrialPlants from './pages/Industries/ManufacturersIndustrialPlants';
import Warehouses from './pages/Industries/Warehouses';
import RecyclingCenters from './pages/Industries/RecyclingCenters';
import './style/App.css';

function RouteReset() {
  const { pathname, key } = useLocation();

  useLayoutEffect(() => {
    const form = document.getElementById('contact-form');
    const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
    // Contact links land at the form, including on mobile where the sidebar
    // stacks above it. Other pages retain their usual scroll-to-top behavior.
    const top = form ? Math.max(0, window.scrollY + form.getBoundingClientRect().top - headerHeight - 24) : 0;
    window.scrollTo({ top, left: 0, behavior: 'instant' });
  }, [pathname, key]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <RouteMetadata />
      <RouteAnalytics />
      <RouteReset />
      <Toaster />
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/products" element={<Products />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/baler-selection-guide" element={<BalerSelectionGuide />} />
        <Route path="/contact/general-inquiry" element={<ContactUs />} />
        <Route path="/contact/request-quote" element={<RequestQuote />} />
        <Route path="/contact/questionnaire" element={<Questionnaire />} />
        <Route path="/contact/baling-wire" element={<BalingWireQuotation />} />
        {contactForms.map((form) => (
          <Route key={form.key} path={form.confirmationPath} element={<ContactPage initialTab={form.key} confirmation />} />
        ))}
        {contactForms.map((form) => (
          <Route key={`${form.key}-legacy-confirmation`} path={form.legacyConfirmationPath} element={<Navigate to={form.confirmationPath} replace />} />
        ))}
        <Route path="/questionnaire" element={<Navigate to="/contact/questionnaire" replace />} />
        <Route path="/request-quote" element={<Navigate to="/contact/request-quote" replace />} />
        <Route path="/contact" element={<Navigate to="/contact/general-inquiry" replace />} />
        <Route path="/contact-us" element={<Navigate to="/contact/general-inquiry" replace />} />
        <Route path="/search" element={<Search />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/submission-success" element={<ContactUs />} />
        <Route path="/closed-end-manual-tie-balers" element={<ClosedEndManualTieBalers />} />
        <Route path="/open-end-automatic-tie-balers" element={<OpenEndAutomaticTieBalers />} />
        <Route path="/wide-mouth-open-end-automatic-tie-balers" element={<WideMouthOpenEndAutomaticTieBalers />} />
        <Route path="/multi-materials-full-eject-closed-end-balers" element={<MultiMaterialsFullEjectClosedEndBalers />} />
        <Route path="/open-end-manual-tie-balers" element={<OpenEndManualTieBalers />} />
        <Route path="/cart-lifters-dumpers-dump-carts" element={<CartLiftersDumpersDumpCarts />} />
        <Route path="/conveyors-bale-conveying-bagging-systems" element={<ConveyorsBaleConveyingBaggingSystems />} />
        <Route path="/used-clothing-balers" element={<UsedClothingBalers />} />
        <Route path="/cotton-gin-textile-baling-presses" element={<CottonGinTextileBalingPresses />} />
        <Route path="/biomass-cotton-gin-trash-baling-press" element={<BiomassCottonGinTrashBalingPress />} />
        <Route path="/vertical-down-packing-baling-presses-balers" element={<VerticalDownPackingBalingPressesBalers />} />
        <Route path="/precrush-compactors-waste-compactors" element={<PreCrushCompactorsWasteCompactors />} />
        <Route path="/custom-designed-specialty-balers" element={<CustomDesignedSpecialtyBalers />} />
        <Route path="/horizontal-manual-tie-balers" element={<HorizontalManualTieBalers />} />
        <Route path="/horizontal-automatic-tie-balers" element={<HorizontalAutomaticTieBalers />} />
        <Route path="/multi-material-full-eject-balers" element={<MultiMaterialFullEjectBalers />} />
        <Route path="/vertical-down-packing-balers" element={<VerticalDownPackingBalers />} />
        <Route path="/service-department" element={<ServiceDepartment />} />
        <Route path="/baler-refurbishing-services" element={<BalerRefurbishingServices />} />
        <Route path="/baler-replacement-parts" element={<BalerReplacementParts />} />
        <Route path="/baling-wire" element={<BalingWire />} />
        <Route path="/baling-wire-quotation" element={<Navigate to="/contact/baling-wire" replace />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/dealers" element={<DealersAndReps />} />
        <Route path="/sustainability" element={<Sustainability />} />
        <Route path="/refurbished" element={<Refurbished />} />
        <Route path="/paper-mills" element={<PaperMills />} />
        <Route path="/corrugated-container-manufacturers" element={<CorrugatedContainerManufacturers />} />
        <Route path="/nonwoven-mills" element={<NonwovenMills />} />
        <Route path="/textile-mills-cotton-gins" element={<TextileMillsCottonGins />} />
        <Route path="/distribution-centers" element={<DistributionCenters />} />
        <Route path="/manufacturers-industrial-plants" element={<ManufacturersIndustrialPlants />} />
        <Route path="/warehouses" element={<Warehouses />} />
        <Route path="/recycling-centers" element={<RecyclingCenters />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <CookieConsent />
    </BrowserRouter>
  );
}

export default App;
